import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Pencil, KeyRound, Ban, CheckCircle2, Search, Lock } from "lucide-react";
import { toast } from "sonner";
import { formatDateTimeID } from "@/lib/format";

const STATUS_STYLE = {
  ACTIVE: "status-active", INACTIVE: "status-inactive", SUSPENDED: "status-suspended", LOCKED: "status-locked",
};

const emptyForm = {
  full_name: "", email: "", username: "", password: "", phone: "", position: "",
  department: "", role: "keuangan", employee_id: "",
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [resetDialog, setResetDialog] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState(null);

  const canManage = me?.role === "super_admin";

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/users", { params });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => {
    api.get("/roles").then(({ data }) => setRoles(data)).catch(() => {});
    api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      full_name: u.full_name || "", email: u.email || "", username: u.username || "",
      password: "", phone: u.phone || "", position: u.position || "",
      department: u.department || "", role: u.role || "keuangan", employee_id: u.employee_id || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.full_name) return toast.error("Nama lengkap wajib diisi.");
    if (!form.email) return toast.error("Email wajib diisi.");
    if (!form.username) return toast.error("Nama pengguna wajib diisi.");
    if (!editing && form.password.length < 8) return toast.error("Kata sandi minimal terdiri dari 8 karakter.");
    if (!form.role) return toast.error("Silakan pilih peran pengguna.");

    try {
      if (editing) {
        const payload = { ...form }; delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Data pengguna berhasil diperbarui.");
      } else {
        await api.post("/users", form);
        toast.success("Pengguna berhasil ditambahkan.");
      }
      setDialogOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeStatus = async (u, action, label) => {
    try {
      await api.post(`/users/${u.id}/${action}`);
      toast.success(label);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doReset = async () => {
    if (newPassword.length < 8) return toast.error("Kata sandi minimal terdiri dari 8 karakter.");
    try {
      await api.post(`/users/${resetDialog.id}/reset-password`, { new_password: newPassword });
      toast.success("Kata sandi berhasil diatur ulang.");
      setResetDialog(null); setNewPassword("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Manajemen Pengguna</h1>
          <p className="text-slate-500 mt-2">Kelola pengguna, peran, dan status akun perusahaan.</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-user-button">
            <Plus className="h-4 w-4 mr-1.5" /> Tambah Pengguna
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_120px] gap-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Cari nama, email, atau nama pengguna..." className="pl-9"
                data-testid="users-search-input"
              />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="users-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="ACTIVE">Aktif</SelectItem>
                <SelectItem value="INACTIVE">Tidak Aktif</SelectItem>
                <SelectItem value="SUSPENDED">Ditangguhkan</SelectItem>
                <SelectItem value="LOCKED">Terkunci</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} data-testid="users-search-button">Cari</Button>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nama Lengkap</TableHead>
                  <TableHead>Email / Nama Pengguna</TableHead>
                  <TableHead>Peran</TableHead>
                  <TableHead>Departemen</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Terakhir Masuk</TableHead>
                  <TableHead className="text-right">Tindakan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Memuat data...</TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Belum ada pengguna.</TableCell></TableRow>
                ) : users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                    <TableCell>
                      <div className="font-medium">{u.full_name}</div>
                      <div className="text-xs text-slate-500">{u.position || "-"}</div>
                    </TableCell>
                    <TableCell>
                      <div>{u.email}</div>
                      <div className="text-xs text-slate-500">{u.username}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="border-slate-300">{u.role_name}</Badge></TableCell>
                    <TableCell>{u.department || "-"}</TableCell>
                    <TableCell><span className={`status-pill ${STATUS_STYLE[u.status] || "status-inactive"}`}>{u.status_label}</span></TableCell>
                    <TableCell className="text-xs text-slate-600">{formatDateTimeID(u.last_login_at)}</TableCell>
                    <TableCell className="text-right">
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-actions-${u.username}`}><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            <DropdownMenuItem onClick={() => openEdit(u)}><Pencil className="h-4 w-4 mr-2" /> Ubah</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setResetDialog(u); setNewPassword(""); }}><KeyRound className="h-4 w-4 mr-2" /> Atur Ulang Kata Sandi</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {u.status === "ACTIVE" ? (
                              <>
                                <DropdownMenuItem onClick={() => setConfirm({ user: u, action: "deactivate", title: "Menonaktifkan Pengguna", desc: "Apakah Anda yakin ingin menonaktifkan pengguna ini?", success: "Pengguna berhasil dinonaktifkan." })}>
                                  <Ban className="h-4 w-4 mr-2" /> Nonaktifkan Pengguna
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setConfirm({ user: u, action: "suspend", title: "Menangguhkan Pengguna", desc: "Apakah Anda yakin ingin menangguhkan pengguna ini?", success: "Pengguna berhasil ditangguhkan." })}>
                                  <Lock className="h-4 w-4 mr-2" /> Tangguhkan Pengguna
                                </DropdownMenuItem>
                              </>
                            ) : (
                              <DropdownMenuItem onClick={() => changeStatus(u, "activate", "Pengguna berhasil diaktifkan.")}>
                                <CheckCircle2 className="h-4 w-4 mr-2" /> Aktifkan Pengguna
                              </DropdownMenuItem>
                            )}
                            {u.status === "LOCKED" && (
                              <DropdownMenuItem onClick={() => changeStatus(u, "unlock", "Pengguna berhasil dibuka kuncinya.")}>
                                <CheckCircle2 className="h-4 w-4 mr-2" /> Buka Kunci Pengguna
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-xs text-slate-500">Total Data: <span className="font-semibold text-slate-700">{users.length}</span></div>
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Ubah Pengguna" : "Tambah Pengguna"}</DialogTitle>
            <DialogDescription>Lengkapi informasi pengguna sesuai kebutuhan.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Nama Lengkap</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="form-full-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" /></div>
            <div className="space-y-1.5"><Label>Nama Pengguna</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="form-username" /></div>
            {!editing && (
              <div className="space-y-1.5"><Label>Kata Sandi</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="form-password" /></div>
            )}
            <div className="space-y-1.5"><Label>Nomor Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>ID Karyawan</Label><Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Jabatan</Label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Departemen</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger><SelectValue placeholder="Pilih departemen" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.code} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Peran</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="form-role"><SelectValue placeholder="Pilih peran" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button onClick={submit} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="form-submit">
              {editing ? "Simpan Perubahan" : "Tambah Pengguna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetDialog} onOpenChange={(v) => !v && setResetDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Atur Ulang Kata Sandi</DialogTitle>
            <DialogDescription>Kata sandi baru untuk {resetDialog?.full_name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Kata Sandi Baru</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(null)}>Batal</Button>
            <Button onClick={doReset} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="reset-password-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={async () => {
                await changeStatus(confirm.user, confirm.action, confirm.success);
                setConfirm(null);
              }}
              data-testid="confirm-action-button"
            >
              Ya, Lanjutkan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
